import React from "react";
import { useNavigate } from "react-router-dom";
import { Star, Building2, User } from "lucide-react";
import Header from "@/components/Header";

export default function ReviewsPage() {
  const navigate = useNavigate();

  const reviews = [
    {
      id: 1,
      name: "Sarah Mitchell",
      location: "Toronto, ON",
      role: "Property Owner",
      rating: 5,
      date: "2 weeks ago",
      review: "Top Prop connected me with an amazing property management company within 24 hours! The process was seamless, and I received quotes from 3 different companies. I'm now working with a fantastic team that handles everything for my rental properties.",
      property: "2 residential properties"
    },
    {
      id: 2,
      name: "James Thompson",
      location: "Ottawa, ON",
      role: "Property Manager",
      rating: 5,
      date: "1 month ago",
      review: "As a property management company, Top Prop has been an excellent source of qualified leads. The property owners who contact us through this platform are serious and ready to make decisions. We've gained 5 new clients in just 2 months!",
      property: "Property Management Company"
    },
    {
      id: 3,
      name: "Linda Chen",
      location: "Mississauga, ON",
      role: "Property Owner",
      rating: 5,
      date: "3 weeks ago",
      review: "I was overwhelmed trying to manage my rental property on my own. Top Prop made it so easy to find professional help. The company I chose has been incredible - they handle tenant issues, maintenance, and rent collection perfectly.",
      property: "Condo unit"
    },
    {
      id: 4,
      name: "Michael Rodriguez",
      location: "Vancouver, BC",
      role: "Property Owner",
      rating: 5,
      date: "1 week ago",
      review: "The 'What's Your Rent Worth?' feature was incredibly helpful! I got rental valuations from multiple property managers and learned my property was worth more than I thought. Now I'm renting it out with professional management in place.",
      property: "Single-family home"
    },
    {
      id: 5,
      name: "Karen Anderson",
      location: "Calgary, AB",
      role: "Property Manager",
      rating: 5,
      date: "2 months ago",
      review: "Top Prop has transformed our business! The quality of leads is outstanding - these are real property owners actively seeking management services. The platform is easy to use and the support team is responsive. Highly recommend!",
      property: "Property Management Company"
    },
    {
      id: 6,
      name: "David Patel",
      location: "Toronto, ON",
      role: "Property Owner",
      rating: 5,
      date: "4 days ago",
      review: "I own 3 rental properties and was spending way too much time dealing with tenants and maintenance. Through Top Prop, I found a management company that specializes in multi-unit properties. My stress level has dropped significantly!",
      property: "3 residential properties"
    },
    {
      id: 7,
      name: "Emily Brown",
      location: "Hamilton, ON",
      role: "Property Owner",
      rating: 5,
      date: "2 weeks ago",
      review: "As a first-time landlord, I was nervous about managing my property. Top Prop connected me with professionals who guided me through everything. They handle tenant screening, leases, and repairs. Best decision I made!",
      property: "Duplex"
    },
    {
      id: 8,
      name: "Robert Johnson",
      location: "Ottawa, ON",
      role: "Property Manager",
      rating: 5,
      date: "3 months ago",
      review: "We've been in property management for 10 years, and Top Prop is the best lead generation service we've used. The leads are pre-qualified and genuinely interested. We've closed deals with 60% of the leads we've received.",
      property: "Property Management Company"
    },
    {
      id: 9,
      name: "Patricia Williams",
      location: "Kitchener, ON",
      role: "Property Owner",
      rating: 5,
      date: "5 days ago",
      review: "I inherited a rental property and had no idea how to manage it. Top Prop made the process so simple! I filled out one form and received calls from qualified property managers. Now my property is fully managed and profitable.",
      property: "Single-family home"
    },
    {
      id: 10,
      name: "Thomas Lee",
      location: "Mississauga, ON",
      role: "Property Owner",
      rating: 5,
      date: "1 month ago",
      review: "The rental valuation service was eye-opening! I received detailed market analyses from 3 different companies. It helped me make an informed decision about renting vs. selling. Top Prop provides real value to property owners.",
      property: "Condo unit"
    }
  ];

  const averageRating = 5.0;
  const totalReviews = reviews.length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F7F7' }}>
      <Header variant="full" />

      {/* Hero Section */}
      <section className="text-white py-16" style={{ backgroundColor: '#1E1E1E' }}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star key={star} className="h-8 w-8 fill-current" style={{ color: '#fbbf24' }} />
              ))}
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              Trusted by Property Owners & Managers
            </h1>
            <p className="text-xl text-gray-300 mb-2">
              {averageRating.toFixed(1)} out of 5 stars • {totalReviews} reviews
            </p>
            <p className="text-lg text-gray-400">
              See what our clients say about their experience with Top Prop
            </p>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-xl transition-shadow duration-300"
                >
                  {/* Stars */}
                  <div className="flex gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className="h-5 w-5 fill-current"
                        style={{ color: '#fbbf24' }}
                      />
                    ))}
                  </div>

                  {/* Review Text */}
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    "{review.review}"
                  </p>

                  {/* Reviewer Info */}
                  <div className="border-t pt-4" style={{ borderColor: '#e5e7eb' }}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <User className="h-5 w-5" style={{ color: '#3b82f6' }} />
                          <p className="font-bold text-gray-900">{review.name}</p>
                        </div>
                        <p className="text-sm text-gray-600">{review.location}</p>
                        <p className="text-xs font-semibold mt-1" style={{ color: '#3b82f6' }}>
                          {review.role}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">{review.property}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-400">{review.date}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Call to Action */}
            <div className="mt-12 text-center">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-2xl font-bold mb-4" style={{ color: '#1E1E1E' }}>
                  Join Hundreds of Satisfied Property Owners
                </h2>
                <p className="text-gray-600 mb-6">
                  Find the perfect property management company for your needs
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => navigate("/get-matched")}
                    className="px-8 py-4 rounded-lg font-bold text-white transition-all duration-300 shadow-md hover:shadow-xl"
                    style={{ backgroundColor: '#3b82f6' }}
                  >
                    I Need Management
                  </button>
                  <button
                    onClick={() => navigate("/whats-your-rent-worth")}
                    className="px-8 py-4 rounded-lg font-bold text-white transition-all duration-300 shadow-md hover:shadow-xl"
                    style={{ background: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)' }}
                  >
                    What's Your Rent Worth?
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-white py-8" style={{ backgroundColor: '#1E1E1E' }}>
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <p className="text-gray-400 text-sm">© 2025 Top Prop. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
